import React from "react";
import searchIcon from "../../assets/SDS.png";
import sopIcon from "../../assets/SOP.png";
import syncIcon from "../../assets/AutoSync.png";
import auditIcon from "../../assets/AuditHistory.png";
import Layout from "../PageLayout/Layout";
import StatsBar from "../shared/StatsBar";
import "./ProductInsurance.css";

const INTEL_FEATURES = [
  {
    title: "Smart Document Search",
    description:
      "Find any doc or message, wherever it lives. Highwatch indexes Slack, Notion, and Drive to surface the right result — no keyword guessing.",
    iconClass: "is-search",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M8 4.5h6.5L18 8v10.5A1.5 1.5 0 0116.5 20H8A1.5 1.5 0 016.5 18.5V6A1.5 1.5 0 018 4.5z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M14.5 4.5V8H18" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9.5 12.5h4M9.5 15h2.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="15.5" cy="15.5" r="2.5" stroke="#fff" strokeWidth="1.6" />
        <path d="M17.5 17.5l1.8 1.8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: "SOP Assistant",
    description:
      "Ask for a process, get the steps. Highwatch turns static SOPs into living workflows — summarized, with clear next actions for your query.",
    iconClass: "is-sop",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7.5 6.5c0-1.38 2.24-2.5 5-2.5s5 1.12 5 2.5v5.5c0 1.38-2.24 2.5-5 2.5s-5-1.12-5-2.5V6.5z" stroke="#fff" strokeWidth="1.6" />
        <path d="M8.5 17.5c1.1 1.2 2.4 1.8 3.5 1.8s2.4-.6 3.5-1.8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="10.5" cy="9.5" r="0.9" fill="#fff" />
        <circle cx="13.5" cy="9.5" r="0.9" fill="#fff" />
        <path d="M11 11.8c.35.35.95.35 1.3 0" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: "Auto-Sync",
    description:
      "Your data stays current, automatically. New uploads and edits sync in real time — no manual tagging, no version chaos.",
    iconClass: "is-sync",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M16.5 7.5l1.8-1.8M7.5 16.5l-1.8 1.8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M18.5 9.5A7 7 0 0012 5.5 7 7 0 005.5 12H4M5.5 14.5A7 7 0 0012 18.5 7 7 0 0018.5 12H20" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Audit History",
    description:
      "Track what was asked, by whom, when. Every interaction is logged for transparency and compliance, without losing usability.",
    iconClass: "is-audit",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12.5" r="6.5" stroke="#fff" strokeWidth="1.6" />
        <path d="M12 9.5v3.2l2.2 1.3" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.5 6.5l-1.2-1.2M15.5 6.5l1.2-1.2" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M9.2 5.8A7 7 0 0112 5a7 7 0 017 7" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
];

const features = [
  {
    icon: searchIcon,
    title: "Smart Document Search",
    description: "Find any doc, policy, or message — no matter where it lives.",
    detail:
      "Highwatch indexes your tools — Slack, Notion, Google Drive — to return the right document or slide without keyword guessing or folder digging.",
    flex: "flex-[2]",
  },
  {
    icon: sopIcon,
    title: "SOP Assistant",
    description: "Ask for a process — get the steps, summarized and ready.",
    detail:
      "Turn static SOPs into living workflows. Highwatch summarizes long documents and extracts next actions based on your query.",
    flex: "flex-[1]",
  },
  {
    icon: syncIcon,
    title: "Auto-Sync",
    description: "Your data, always up-to-date — no effort required.",
    detail:
      "New uploads, changes, and edits in connected tools sync in real time. No manual tagging. No version chaos.",
    flex: "flex-[1]",
  },
  {
    icon: auditIcon,
    title: "Audit History",
    description: "Track what was asked, by whom, and when.",
    detail:
      "Every interaction is logged for transparency, governance, and compliance — without losing usability.",
    flex: "flex-[2]",
  },
];

const ROLES = [
  {
    label: "Underwriters",
    cardClass: "is-underwriters",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="8" r="3.5" stroke="#fff" strokeWidth="1.8" />
        <path d="M5.5 19c1.2-3 3.4-4.5 6.5-4.5S17.8 16 19 19" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="17.5" cy="10.5" r="2.2" fill="#14b8a6" stroke="#fff" strokeWidth="1.2" />
      </svg>
    ),
  },
  {
    label: "Claims adjusters",
    cardClass: "is-claims",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 3.5h7l4 4V20a1.5 1.5 0 01-1.5 1.5H7A1.5 1.5 0 015.5 20V5A1.5 1.5 0 017 3.5z" stroke="#fff" strokeWidth="1.8" />
        <path d="M14 3.5V8h4.5" stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M9 13.5l2 2 4-4.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Compliance teams",
    cardClass: "is-compliance",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3.5l7 3v5.2c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6.5l7-3z" stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M9.2 12.2l1.9 1.9 3.8-3.9" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Executives",
    cardClass: "is-executives",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3.5" y="5" width="17" height="12" rx="1.5" stroke="#fff" strokeWidth="1.8" />
        <path d="M8 20h8M12 17v3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M7.5 13.5l3-3 2.5 2.5 3.5-4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const PROBLEMS = [
  {
    num: "01",
    title: "Fragmented knowledge",
    text: "Submission, policy, and claims data sit in separate systems with no shared context — slowing every decision that depends on them.",
  },
  {
    num: "02",
    title: "Slow, manual review",
    text: "Underwriters and adjusters spend hours per case on manual document review instead of judgment-driven decisions.",
  },
  {
    num: "03",
    title: "Opaque AI decisions",
    text: "Most AI tools can't explain their reasoning — a non-starter for regulated organizations that must justify every decision.",
  },
];

const Card = ({ feature }) => {
  const isWide = feature.flex === "flex-[2]";

  return (
    <div className={`bg-white rounded-2xl ${feature.flex} min-w-[280px] flex flex-col`}>
      <div className="shadow-sm hover:shadow-md transition-shadow rounded-2xl overflow-hidden p-2 flex-1">
        <div className="border border-gray-200 rounded-2xl p-2 bg-white flex flex-col h-full overflow-hidden">
          <div className="flex flex-col h-full rounded-2xl overflow-hidden">
            <div
              className={`w-full flex items-center justify-center bg-[#F7F7F8] ${
                isWide ? "h-[240px]" : "h-[200px]"
              } rounded-xl flex-shrink-0`}
            >
              <img
                src={feature.icon}
                alt={feature.title}
                className="max-h-full max-w-full object-contain"
              />
            </div>
            <div className="p-4 flex flex-col gap-2 flex-1 justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-700">{feature.description}</p>
              </div>
              <p className="text-sm text-gray-500">{feature.detail}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeaturesSection = () => {
  return (
    <>
      {/* Mobile — unchanged */}
      <section className="pi-mobile">
        <Layout>
          <div className="py-16 px-0 sm:px-2">
            <div className="max-w-[621px] mx-auto mb-16 text-center">
              <div className="internal-hero-badge mb-6">
                <img src="/icons/bolt.svg" alt="bolt Icon" />
                Key Features
              </div>
              <h2 className="text-[32px] sm:text-[40px] leading-tight font-bold text-gray-900 mb-4">
                Intelligence that Works. Simplicity that Scales.
              </h2>
              <p className="text-gray-600 text-base leading-[26px] tracking-[-0.01em]">
                Highwatch unifies search, SOPs, and compliance under one intelligent system – built for clarity, speed, and enterprise trust.
              </p>
            </div>

            <div className="flex flex-col gap-6">
              <div className="flex flex-col lg:flex-row gap-6">
                <Card feature={features[0]} />
                <Card feature={features[1]} />
              </div>
              <div className="flex flex-col lg:flex-row gap-6">
                <Card feature={features[2]} />
                <Card feature={features[3]} />
              </div>
            </div>
          </div>
        </Layout>
      </section>

      {/* Desktop — Product Intelligence key features + stats */}
      <div className="pi-intel-desktop">
        <section id="pi-intel-features" className="pi-intel-features">
          <div className="pi-intel-features__header">
            <p className="pi-eyebrow">KEY FEATURES</p>
            <h2>
              Intelligence that Works.
              <br />
              Simplicity that Scales.
            </h2>
            <p className="pi-intel-features__lead">
              Highwatch unifies search, SOPs, and compliance under one intelligent system — built
              for clarity, speed, and enterprise trust.
            </p>
          </div>
          <div className="pi-intel-features__grid">
            {INTEL_FEATURES.map((feature) => (
              <article
                key={feature.title}
                className={`pi-intel-features__card pi-intel-features__card--${feature.iconClass}`}
              >
                <div className="pi-intel-features__head">
                  <div className="pi-intel-features__icon" aria-hidden="true">
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                </div>
                <p className="pi-intel-features__desc">{feature.description}</p>
              </article>
            ))}
          </div>
        </section>
        <StatsBar />
      </div>

      {/* Desktop — roles + problem + stats */}
      <section className="pi-desktop pi-roles">
        <h2>
          Built for the people who run
          <br />
          insurance operations
        </h2>
        <div className="pi-roles__grid">
          {ROLES.map((role) => (
            <article key={role.label} className={`pi-roles__card ${role.cardClass}`}>
              <div className="pi-roles__icon" aria-hidden="true">
                {role.icon}
              </div>
              <strong>{role.label}</strong>
            </article>
          ))}
        </div>
      </section>

      <section className="pi-desktop pi-problem">
        <div className="pi-problem__inner">
          <p className="pi-eyebrow">THE PROBLEM</p>
          <h2>
            Insurance knowledge is fragmented
            <br />
            across systems, teams, and decisions.
          </h2>
          <p className="pi-problem__lead">
            Submissions, claims, policy data, and risk signals live in disconnected
            tools. Underwriters re-key the same information across systems.
            Compliance teams reconstruct decisions after the fact. Generic AI tools
            cannot solve this because they lack organizational context, deep system
            integration, and compliance readiness.
          </p>
          <div className="pi-problem__grid">
            {PROBLEMS.map((item) => (
              <article key={item.num} className="pi-problem__card">
                <em>{item.num}</em>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <StatsBar className="pi-desktop" />
    </>
  );
};

export default FeaturesSection;
